import React, { Component } from 'react';

import Animator from './Animator';
import MUTIL from './MUTIL';

/* some SVG elements must refer to others by id; here's a simple id generator
 * */
const mk_svg_id_and_url = (() => {
	var next_id = 1;
	return () => {
		const id = `X${next_id++}`;
		const url = `url(#${id})`
		return [id, url];
	};
})();

/* animated <clipPath><rect.../></clipPath>. use it to present something!
 * props:
 *  - id:               as in <clipPath id={id}>; you need it later
 *  - direction:        which way to present from
 *  - duration:         duration of animation (in seconds)
 *  - t0:               set this to something unique to restart animation
 *  - x/y/width/height: position/dimensions where the animation will end
 */
class PresentingRectClipPath extends Component {
	constructor(props) {
		super(props);
		this.state = { u: 0 };
	}

	componentDidMount() {
		this.animator = new Animator({
			speed: 1/this.props.duration,
			callback: (u) => this.setState({u})
		});
	}

	componentWillUnmount() {
		if (this.animator) this.animator.kill();
	}

	componentDidUpdate(prev_props) {
		if (this.animator && this.props.t0 !== prev_props.t0) {
			this.animator.set_value_and_target(0, 1);
		}
	}

	render() {
		const x1 = this.props.x1 || 0;
		const y1 = this.props.y1 || 0;
		const x2 = this.props.x2;
		const y2 = this.props.y2;
		const width = x2 - x1;
		const height = y2 - y1;

		const u = this.state.u;

		var rect;
		switch (this.props.direction) {
		case "right":
			rect = <rect x={x1} y={y1} width={u*width} height={height}/>;
			break;
		default:
			break;
		}

		return <clipPath id={this.props.id}>{rect}</clipPath>;
	}
}


/* <linearGradient><stop>...</linearGradient> helper
    the "stops" property in an array of 2-to-3 element arrays on the form:
      [<offset>, <color or class>[, opacity]]
    if "class" starts with "." it's considered to be a CSS class. examples
      [[0, ".ad_chart_line_x0"], [1, ".ad_chart_line_x1"]]
      [[0, "#ff0000", 0.5], [1, "#0000ff", 0.9]]
*/
function LinearGradient(props) {
	const stops = props.stops.map((stop) => {
		var props = {offset: stop[0]};
		const stopval = stop[1];
		var col = undefined;
		if (typeof stopval === "string") {
			const first_char = stopval[0];
			if (first_char === ".") {
				props.className = stop[1].slice(1);
			} else {
				col = MUTIL.hex2col(stopval);
			}
		} else if (typeof stopval === "object") {
			col = stopval;
		}

		if (col !== undefined) {
			var opacity = undefined;
			if (col.length === 3) {
				props.stopColor = MUTIL.col2hex(col);
			} else {
				props.stopColor = MUTIL.col2hex(col.slice(0,3));
				opacity = col[3];
			}
			if (opacity !== undefined) props.stopOpacity = opacity;
		}

		if (stop[2] !== undefined) props.stopOpacity = stop[2];

		return <stop key={"ofz:"+props.offset} {...props}/>
	});
	return (
		<linearGradient
			id={props.id}
			x1={props.x1}
			y1={props.y1}
			x2={props.x2}
			y2={props.y2}
			gradientUnits="userSpaceOnUse"
		>
			{stops}
		</linearGradient>
	);
}

function points2string(points) {
	return points.map(p => `${p[0]},${p[1]}`).join(" ");
}

function calc_text_width(text_class, texts) {
	if (typeof texts === "string") texts = [texts];
	var mk = (tag, attrs, html) => {
		var element = document.createElementNS('http://www.w3.org/2000/svg', tag);
		if (attrs) for (const k in attrs) element.setAttributeNS(null, k, attrs[k]);
		if (html) element.innerHTML = html;
		return element;
	}

	var svg = mk("svg");
	var body = window.document.body;
	body.appendChild(svg);
	var width = 0;
	for (const text of texts) {
		var text_tag = mk("text", {x: 0, y: 0, "class": text_class}, text);
		svg.appendChild(text_tag);
		width = Math.max(width, text_tag.getBoundingClientRect().width);
		svg.removeChild(text_tag);
	}
	body.removeChild(svg);
	return width;
}

/* Simple SVG transform attribute constructor. see:
 * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/transform
 * NOTE that many SVG transforms allow optional arguments, and so do we; e.g.
 * you don't have to pass x/y to rotate(a,x,y) */
class TransformBuilder {
	constructor(str) {
		this.str = str || "";
	}

	_chain() {
		const argv = [...arguments]; // "arguments.slice is not a function", blah blah
		var element = argv[0] + "(" + argv.slice(1).filter(x => x !== undefined).join(" ") + ")";
		var new_str;
		if (this.str.length > 0) {
			new_str = this.str + " " + element;
		} else {
			new_str = element;
		}
		return new TransformBuilder(new_str);
	}

	translate(x, y) { return this._chain("translate", x, y); }
	rotate(a, x, y) { return this._chain("rotate", a, x, y); }
}

const TX = new TransformBuilder();

class PathBuilder {
	constructor(str) {
		this.str = str || "";
	}

	_chain() {
		const argv = [...arguments]; // "arguments.slice is not a function", blah blah
		const mapper = (x) => {
			if (typeof x === "boolean") return x?1:0;
			return x;
		};
		var element = argv[0] + argv.slice(1).map(mapper).join(" ");
		var new_str;
		if (this.str.length > 0) {
			new_str = this.str + " " + element;
		} else {
			new_str = element;
		}
		return new PathBuilder(new_str);
	}

	M(x, y) { return this._chain("M", x, y); }
	L(x, y) { return this._chain("L", x, y); }
	l(dx, dy) { return this._chain("l", dx, dy); }
	A(rx, ry, x_axis_rotation, large_arc_flag, sweep_flag, x, y) {
		return this._chain("A", rx, ry, x_axis_rotation, large_arc_flag, sweep_flag, x, y);
	}
}

const Path = new PathBuilder();

const wedge_calc = (value, r, start_angle) => {
	value = Math.min(1, Math.max(0, value));
	if (value === 1) {
		return {
			circle: {r},
		};
	} else {
		const outer = [
			[0,0],
			MUTIL.v2_scale(MUTIL.v2_degrees(start_angle), r),
			MUTIL.v2_scale(MUTIL.v2_degrees(start_angle + value*360), r),
		];

		const d = Path
			.M(outer[0][0], outer[0][1])
			.L(outer[1][0], outer[1][1])
			.A(r, r, 0, value > 0.5, true, outer[2][0], outer[2][1])
			.L(outer[0][0], outer[0][1])
			.str;

		return {
			path: {d},
		};
	}
};

export {
	mk_svg_id_and_url,
	PresentingRectClipPath,
	LinearGradient,
	points2string,
	calc_text_width,
	TX,
	Path,
	wedge_calc,
}



// WEBPACK FOOTER //
// ./src/SVGUtil.js